import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSelector, useDispatch } from "react-redux";
import "./Draft.css";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DraftMessagesActions, archivedActions } from "../../_store";
import ConfirmationModal from "../../components/common/UI/Inputs/NotificationModal/BootstrapModalPopup";
import NotificationService from "../../../src/ui/ui-modal-notification";
import { ReactNotifications } from "react-notifications-component";
import useDraft from "../../components/common/autoSaveDraftMsg/useDraft";
import translations from "../../config/TranslateFile.jsx";
import { EventLog } from "../../components/common/EventLog/EventLog.jsx";
import useFullPageLoader from "../../hooks/useFullPageLoader.jsx";
import { CreateEventLogTextFileForInbox } from "../../components/common/EventLog/EventLog.jsx";
import { storeCurrentRoute } from "../../utils/routeTracker.js";
import { getStoredValue } from "../../_helpers/storagehelper.js";

function Draft() {
  const userID = getStoredValue("userID"); // PublicUserID get from local storage
  const defaultParams = useMemo(
    () => ({
      userID: userID,
      pageNo: 1,
      rowCountPerPage: 10,
      sortField: "",
    }),
    [userID]
  );
  const currentLang = translations();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(defaultParams.pageNo);
  const [showModal, setShowModal] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [inmateName, setInmateName] = useState("");
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [modalMessages, setModalMessages] = useState([]);
  const [showConnectionModalPopup, setshowConnectionModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lastItemRef = useRef(null);
  const { DraftMessagesList, loading, error } = useSelector(
    (state) => state.draft || {}
  );
  // Auto Draft save
  const { isSaveDraftModified, setIsSaveDraftModified } = useDraft();
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [isloading, setisloading] = useState(false);

  useEffect(() => {
    const savedDraft = getStoredValue("draft");
    if (savedDraft != null) {
      setIsSaveDraftModified(true);
    }
  }, [setIsSaveDraftModified]);

  const loadInitialData = useCallback(
    (pageNo = currentPage) => {
      showLoader(); setisloading(true);
      dispatch(
        DraftMessagesActions.LoadDraftMessages({
          ...defaultParams,
          pageNo,
        })
      ).finally(() => {
        hideLoader(); setisloading(false) // Hide loader after operation completes
      });

      CreateEventLogTextFileForInbox(
        dispatch,
        `Load Draft Message List - Draft Menu`,
        "---",
        JSON?.stringify(DraftMessagesList),
        "Draft Message List Count - " + DraftMessagesList?.length
      );
    },
    [dispatch, defaultParams, currentPage]
  );

  // Load initial draft messages on component mount or page change
  // UseEffect to load data on mount and page change
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData, isSaveDraftModified]);

  // Updates the `searchTerm` state with the value from the search input field when the user types.
  //const [searchResult, setSearchResult] = useState(false);

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    //setSearchResult(true)
    if (value.trim() === "") {
      setHasMoreData(true);
    } else {
      setHasMoreData(false);
    }
  };

  // Filters `DraftMessagesList` by `searchTerm` in `MessageTo` or `MessageSubject` and memoizes the result for performance optimization.
  const filteredDrafts = useMemo(() => {
    setHasMoreData(true);
    if (DraftMessagesList?.length < 10) {
      setHasMoreData(false);
    }
    return DraftMessagesList?.filter(
      (draft) =>
        draft.MessageTo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        draft.MessageSubject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        draft.CreatedDate.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [DraftMessagesList, searchTerm]);


  const handleLoadMore = useCallback(async () => {
    const lastItem = lastItemRef.current;
    const prevOffsetTop = lastItem?.offsetTop || 0;

    try {
      const newVisibleCount = visibleCount + 10;
      const nextPage = Math.ceil(
        newVisibleCount / defaultParams.rowCountPerPage
      );

      showLoader(); setisloading(true)

      const response = await dispatch(
        DraftMessagesActions.LoadDraftMessages({
          ...defaultParams,
          pageNo: nextPage,
          append: true,
        })
      );

      const payload = response?.payload || [];

      if (payload.length > 0) {
        setVisibleCount(newVisibleCount);
      }

      if (payload.length < defaultParams.rowCountPerPage) {
        setHasMoreData(false);
      }

      // Restore scroll to last known item
      setTimeout(() => {
        window.scrollTo({
          top: prevOffsetTop,
          behavior: "auto",
        });
      }, 0);
    } catch (error) {
      console.error("Failed to load more drafts:", error);
      toast.error("Something went wrong while loading more drafts.");
      setHasMoreData(false);
    } finally {
      hideLoader(); setisloading(false)
    }
  }, [visibleCount, defaultParams, dispatch]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);


  const handleDraftClick = (draft) => {
    setSelectedDraft(draft);
    setInmateName(draft.MessageTo);
    setShowModal(true);
  };

  const handleConfirm = () => {
    handleConfirmcanceledConnection();
  };

  const handleDiscardDraftClick = (draft) => {
    setSelectedDraft(draft);
    setShowDiscardModal(true); // Show discard confirmation modal
  };

  const handleCancelDiscardDraft = () => {
    setShowDiscardModal(false); // Close the discard confirmation modal
  };

  const handleCancelPopup = () => {
    setshowConnectionModal(false);
  };

  const handleConfirmDiscardDraft = () => {
    if (selectedDraft.DraftID) {
      dispatch(DraftMessagesActions.DiscardDraftMessages(selectedDraft.DraftID))
        .then(() => {
          // Adjust the current page if deleting the last item on the current page
          if (filteredDrafts?.length === 1 && currentPage > 1) {
            setCurrentPage((prev) => prev - 1); // Decrement currentPage
          }
          setShowDiscardModal(false);

          NotificationService({
            title: "Success",
            message: "Draft deleted successfully",
            type: "success",
          });

          CreateEventLogTextFileForInbox(
            dispatch,
            `Discard Draft Message ( Draft ID - ${selectedDraft?.DraftID} )`,
            "---",
            "---",
            filteredDrafts?.length != null
              ? `Discard draft message successfully. Return value - ${filteredDrafts?.length}`
              : "0"
          );
          const publicUserId = getStoredValue("userID");
          dispatch(
            archivedActions.PULoginUnreadNotificationCount({ publicUserId })
          );
          // Trigger reloading of the data
          loadInitialData(currentPage); // Use updated page
        })
        .catch((error) => {
          console.error("Failed to delete draft:", error);
          NotificationService({
            title: "Error",
            message: "Failed to delete draft",
            type: "danger",
          });
        });
    }
  };

  const handleConfirmcanceledConnection = () => {
    const messages = [];

    if (
      selectedDraft.InmateID &&
      Number.isInteger(Number(selectedDraft.InmateID)) &&
      parseInt(selectedDraft.InmateID) > 0
    ) {
      const isMessageAccessRestricted = selectedDraft.MessageAccess === "0";
      const isInmateReleased = selectedDraft.InmateStatus === "0";
      const isConnectionCanceled = ["3", "4", "5"].includes(
        String(selectedDraft.ConnectionStatusID)
      );

      if (
        isMessageAccessRestricted ||
        isInmateReleased ||
        isConnectionCanceled
      ) {
        if (selectedDraft?.InmateStatus == "0") {
          messages.push(`${inmateName} has been released from the prison.`);
        } else if (selectedDraft?.ConnectionStatusID == "3") {
          messages.push(`Connection with ${inmateName} is canceled by you.`);
        }

        else if (selectedDraft?.ConnectionStatusID == "4") {
          messages.push(`Connection with ${inmateName} has been rejected by the inmate.`);
        }

        else if (selectedDraft?.ConnectionStatusID == "5") {
          messages.push(`Connection with ${inmateName} has been blocked by the admin.`);
        }

        else if (selectedDraft?.MessageAccess === "0") {
          messages.push("The Permission to send Message to this contact has been restricted. .");
        }

        setModalMessages(messages);
        setShowModal(false);
        setshowConnectionModal(true);
        return;
      }
    }

    // If no issues, proceed with redirection logic
    if (selectedDraft.DraftFromValue === 1) {
      navigate("/public/Inbox");
      setTimeout(() => {
        navigate("/public/Messenger", {
          state: {
            MessageID: selectedDraft.MessageID,
            PublicUserID: selectedDraft.PublicUserID,
            MessageBody: selectedDraft.MessageBody,
            DraftID: selectedDraft.DraftID,
            from: "/public/Inbox",
          },
        });
      }, 100);
      storeCurrentRoute("/public/Messenger");

      const infoMessage = `Redirect into Inbox Menu to load Draft Message ( Draft ID - ${selectedDraft?.DraftID} )`;
      const redirectURL = `${window.location.href}?PUDraftMessageID=${selectedDraft?.DraftID}`;
      EventLog(dispatch, infoMessage, redirectURL, "---", "---");
    } else if (selectedDraft.DraftFromValue === 2) {
      navigate("/public/Archived");
      setTimeout(() => {
        navigate("/public/Messenger", {
          state: {
            MessageID: selectedDraft.MessageID,
            PublicUserID: selectedDraft.PublicUserID,
            MessageBody: selectedDraft.MessageBody,
            DraftID: selectedDraft.DraftID,
            from: "/public/Archived",
          },
        });
      }, 100);
      storeCurrentRoute("/public/Messenger");

      const infoMessage = `Redirect into Archived Menu to load Draft Message ( Draft ID - ${selectedDraft?.DraftID} )`;
      const redirectURL = `/public/Messenger?PUDraftMessageID=${selectedDraft?.DraftID}`;
      const Response = "---";
      const Status = "---";
      EventLog(dispatch, infoMessage, redirectURL, Response, Status);
    } else if (selectedDraft.DraftFromValue === 3) {
      navigate("/public/WriteMessage", {
        state: {
          MessageTo: selectedDraft.InmateID,
          MessageSubject: selectedDraft.MessageSubject,
          MessageBody: selectedDraft.MessageBody,
          DraftID: selectedDraft.DraftID,
        },
      });
      storeCurrentRoute("/public/WriteMessage");

      const infoMessage = `Redirect into Write Message Menu to load Draft Message ( Draft ID - ${selectedDraft?.DraftID} )`;
      const redirectURL = `/public/WriteMessage?DraftMessageForInmateID=${selectedDraft?.DraftID}`;
      const Response = "---";
      const Status = "---";
      EventLog(dispatch, infoMessage, redirectURL, Response, Status);
    }

    setshowConnectionModal(false);
  };

  return (
    <>
      {" "}
      <ReactNotifications />
      <div className="">
        {loader}
        <div className="header-title">
          <h5>
            {/* <i className="fa fa-pencil-square me-2"></i> */}
            {currentLang?.Drafts}
          </h5>
        </div>
      </div>
      <div className="card-body py-3 mb-5">
        <div className="col-md-12 mt-n3 ">
          <ul
            className="list-group shadow-sm border-radius-all"
            id="divLoadList"
          >
            <li className="list-group-item" id="draftgrouplist">
              <div className="">
                <div className="d-flex align-items-center m-w-100">
                  <div className="position-relative p-3 w-40">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={currentLang?.Mycontacts?.Search}
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </li>
            {loading ? null : error ? (
              <div className="alert alert-danger text-center" role="alert">
                {error}
              </div>
            ) : filteredDrafts?.length === 0 ? (
              <div className="alert-light-red mb-3 font-size-16 p-3 text-center">
                {currentLang?.NO_Data_Found}
              </div>
            ) : (
              filteredDrafts?.slice(0, visibleCount).map((draft, index, array) => (
                <li
                  className="list-group-item cursor-pointer"
                  key={index}
                  ref={index === array.length - 1 ? lastItemRef : null}
                  onClick={() => handleDraftClick(draft)}
                >
                  <div className="d-flex justify-content-between align-items-center  flex-wrap">
                    <div className="d-flex align-items-center  m-w-100 mo-mb-10">
                      <i
                        className="fa fa-user-circle fa-2x me-3"
                        aria-hidden="true"
                      ></i>
                      <div>
                        <label className="fw-bold mb-0 profile-Name">
                          {draft.MessageTo}
                        </label>
                        <br />
                        <span
                          className="text-muted msg-desc hidden-text"
                          dangerouslySetInnerHTML={{
                            __html: draft.MessageSubject,
                          }}
                        />
                      </div>
                    </div>
                    <div className="text-end">
                      <span className="text-muted float-right position-mobile">
                        {draft.CreatedDate}
                      </span>
                      <button
                        type="button"
                        title={currentLang?.Draft?.Discard_Draft}
                        className="btn draftbtn"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDiscardDraftClick(draft);
                        }}
                      >
                        {currentLang?.Draft?.Discard_Draft}
                      </button>
                    </div>
                  </div>
                </li>
              ))
            )}
            {(!isloading && filteredDrafts?.length >= 10 && hasMoreData) && (
              <li className="list-group-item">
                <div className="d-flex justify-content-center mb-n3">
                  <div className="div-load-more-data inbox panel footer-load-more">
                    <label
                      className="text-decoration-none fs-5 cursor-pointer load-more load-more-button"
                      onClick={handleLoadMore}
                    >
                      <i class="fa-solid fa-plus me-1 font-size-14"></i> {currentLang?.Draft?.Load_More}
                    </label>
                  </div>
                </div>
              </li>
            )}
          </ul>
        </div>

        {/* ------- Popup for - Draft Item Click  -------------- */}
        <ConfirmationModal
          show={showModal}
          onHide={() => setShowModal(false)} // Correctly passes the close handler
          onConfirm={handleConfirm}
          title={currentLang?.Message}
          message={currentLang?.DraftPopContent?.Redirect_Page}
          onconfirmname={currentLang?.buttons?.Yes}
          oncancelname={currentLang?.buttons?.No}
        />

        {/* ------- Popup for - Discard Draft -------------- */}
        <ConfirmationModal
          show={showDiscardModal}
          onHide={handleCancelDiscardDraft}
          onConfirm={handleConfirmDiscardDraft}
          title={currentLang?.Message}
          message={currentLang?.DraftPopContent?.Discard_Draft}
          onconfirmname={currentLang?.buttons?.Yes}
          oncancelname={currentLang?.buttons?.No}
        />
        {/* ------- Popup for - Connection canceled  -------------- */}
        <ConfirmationModal
          show={showConnectionModalPopup}
          onConfirm={handleCancelPopup}
          title={currentLang?.Message}
          message={modalMessages.map((msg, index) => (
            <p key={index}>{msg}</p>
          ))}
          showCancelButton={false}
          closeButton={false}
          size="lg"
          onconfirmname={currentLang?.buttons?.OK}
          oncancelname={currentLang?.buttons?.No}
        />
        <ToastContainer />
      </div>
    </>
  );
}

export default React.memo(Draft);
